import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import NewLeadDialog from "@/components/board/NewLeadDialog";
import { toast } from "sonner";
import { exportCsv } from "@/lib/csv";
import { wsPipeline, isRecruiting } from "@/lib/workspace";
import {
  daysBetween,
  deriveInitials,
  isNextActionDue,
  isOverdueInStage,
} from "@/lib/pipeline";
import { mcoColor, ownerColor, stageColor } from "@/lib/palette";
import {
  ArrowUpDown,
  AlertTriangle,
  Ban,
  Bookmark,
  Clock,
  FileDown,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/roles";

const SORT_FIELDS = {
  full_name: (c) => (c.full_name || "").toLowerCase(),
  av_id: (c) => c.av_id,
  current_stage: (c) => c.current_stage,
  payer_mco: (c) => c.payer_mco,
  owner: (c, users) => (users.find((u) => u.id === c.owner_id)?.name || "").toLowerCase(),
  days: (c) => daysBetween(c.stage_entered_at),
  next_action: (c) => c.next_action_date || "9999",
};

export default function ClientsListPage() {
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [q, setQ] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [stateFilter, setStateFilter] = useState("");
  const [sortField, setSortField] = useState("av_id");
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const { user } = useAuth();
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveScope, setSaveScope] = useState("private");
  const [editCell, setEditCell] = useState(null); // {cardId, field}
  const [editVals, setEditVals] = useState({});

  const load = () =>
    Promise.all([
      api.get("/cards", { params: { status: "active", pipeline: wsPipeline() } }),
      api.get("/cards", { params: { status: "archived", pipeline: wsPipeline() } }),
      api.get("/templates", { params: { pipeline: wsPipeline() } }),
      api.get("/users"),
      api.get("/lists"),
      api.get("/saved-filters"),
    ]).then(([a, b, t, u, l, sf]) => {
      setCards([...a.data, ...b.data]);
      setTemplates(t.data);
      setUsers(u.data);
      setLists(l.data);
      setSavedFilters(sf.data);
    });

  useEffect(() => { load(); }, []);

  const currentFilter = { q, ownerFilter, stageFilter, statusFilter, stateFilter, sortField, sortDir };
  const applySaved = (f) => {
    const flt = f.filter || {};
    setQ(flt.q || "");
    setOwnerFilter(flt.ownerFilter || "");
    setStageFilter(flt.stageFilter || "");
    setStatusFilter(flt.statusFilter || "active");
    setStateFilter(flt.stateFilter || "");
    setSortField(flt.sortField || "av_id");
    setSortDir(flt.sortDir || "asc");
    toast.success(`Applied "${f.name}"`);
  };
  const saveCurrent = async () => {
    if (!saveName.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post("/saved-filters", { name: saveName.trim(), scope: saveScope, filter: currentFilter });
      setSavedFilters((p) => [data, ...p]);
      setSaveOpen(false); setSaveName(""); setSaveScope("private");
      toast.success("Filter saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteSaved = async (id) => {
    if (!confirm("Delete this saved filter?")) return;
    try {
      await api.delete(`/saved-filters/${id}`);
      setSavedFilters((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tplByStage = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.stage_number, t])),
    [templates]
  );

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    let arr = cards.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (ownerFilter && c.owner_id !== ownerFilter) return false;
      if (stageFilter && c.current_stage !== Number(stageFilter)) return false;
      if (stateFilter === "overdue" && !isOverdueInStage(c, tplByStage[c.current_stage])) return false;
      if (stateFilter === "blocked" && !c.blocked) return false;
      if (stateFilter === "next_due" && !isNextActionDue(c)) return false;
      if (s) {
        const blob = `${c.full_name || ""} ${c.av_id} ${c.payer_mco} ${tplByStage[c.current_stage]?.name || ""}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
    const fn = SORT_FIELDS[sortField] || SORT_FIELDS.av_id;
    arr = [...arr].sort((a, b) => {
      const av = fn(a, users);
      const bv = fn(b, users);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [cards, q, ownerFilter, stageFilter, statusFilter, stateFilter, sortField, sortDir, tplByStage, users]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const csvExport = () => {
    exportCsv(
      `avida-clients-${new Date().toISOString().slice(0, 10)}.csv`,
      visible.map((c) => ({
        av_id: c.av_id,
        full_name: c.full_name,
        status: c.status,
        stage: c.current_stage,
        stage_name: tplByStage[c.current_stage]?.name,
        payer_mco: c.payer_mco,
        referral_partner: c.referral_partner_name || c.referral_source || "",
        event_campaign: c.marketing_effort_name || "",
        field_agent: c.field_agent_name || "",
        owner: users.find((u) => u.id === c.owner_id)?.name || "",
        days_in_stage: Math.floor(daysBetween(c.stage_entered_at)),
        blocked: c.blocked,
        blocked_reason: c.blocked_reason || "",
        next_action_date: c.next_action_date || "",
        archived_reason: c.archived_reason || "",
      }))
    );
  };

  const Th = ({ field, children, className = "" }) => (
    <th
      data-testid={`th-${field}`}
      onClick={() => toggleSort(field)}
      className={`text-left px-4 py-3 cursor-pointer select-none hover:bg-[#15233B]/5 ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? "text-[#FBBF24]" : "text-[#15233B]/30"}`} />
      </span>
    </th>
  );

  const onCardChanged = (updated) => {
    setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelected((s) => (s && s.id === updated.id ? updated : s));
  };

  const openEdit = (e, cardId, field, init) => {
    e.stopPropagation();
    setEditCell({ cardId, field });
    setEditVals(init || {});
  };
  const closeEdit = () => {
    setEditCell(null);
    setEditVals({});
  };
  const saveInlineOwner = async (cardId, owner_id) => {
    try {
      const { data } = await api.patch(`/cards/${cardId}`, { owner_id });
      onCardChanged(data);
      toast.success("Owner updated");
    } catch (e) { toast.error(formatApiError(e)); }
    closeEdit();
  };
  const saveInlineStage = async (cardId, stage_number) => {
    try {
      const { data } = await api.post(`/cards/${cardId}/set-stage`, { stage_number: Number(stage_number) });
      onCardChanged(data);
      toast.success(`Moved to stage ${stage_number}`);
    } catch (e) { toast.error(formatApiError(e)); }
    closeEdit();
  };
  const saveInlineNextAction = async (cardId) => {
    try {
      const { data } = await api.patch(`/cards/${cardId}`, {
        next_action_date: editVals.next_action_date || null,
        next_action_text: editVals.next_action_text || "",
      });
      onCardChanged(data);
      toast.success("Next action saved");
    } catch (e) { toast.error(formatApiError(e)); }
    closeEdit();
  };

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">{isRecruiting() ? "Candidates" : "Clients"}</h1>
            <p className="text-sm text-[#15233B]/60">Sortable, searchable list view of every client. <span className="text-[#FBBF24] font-semibold">Click stage / owner / next-action to edit inline.</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={csvExport}
              data-testid="clients-csv-export"
              className="px-3 py-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1"
            >
              <FileDown className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button
              onClick={() => setShowNewLead(true)}
              data-testid="clients-new-lead-button"
              className="px-3 py-2 rounded-md bg-[#FFCB05] hover:bg-[#FBC02D] text-[#15233B] text-xs font-extrabold flex items-center gap-1 shadow-sm ring-1 ring-[#FFCB05]/40"
            >
              <Plus className="h-3.5 w-3.5" /> New lead
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#15233B]/10 rounded-md p-3 flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#15233B]/40" />
            <input
              data-testid="clients-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, AV-ID, MCO, stage…"
              className="w-full pl-9 pr-3 py-2 border border-[#15233B]/15 rounded text-sm bg-white"
            />
          </div>
          <select
            data-testid="clients-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-2 text-xs bg-white font-semibold"
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
          <select
            data-testid="clients-owner-filter"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-2 text-xs bg-white font-semibold"
          >
            <option value="">All owners</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select
            data-testid="clients-stage-filter"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-2 text-xs bg-white font-semibold"
          >
            <option value="">All stages</option>
            {templates.map((t) => (
              <option key={t.stage_number} value={t.stage_number}>
                {t.stage_number} · {t.name}
              </option>
            ))}
          </select>
          <select
            data-testid="clients-state-filter"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="border border-[#15233B]/15 rounded px-2 py-2 text-xs bg-white font-semibold"
          >
            <option value="">Any state</option>
            <option value="overdue">Overdue in stage</option>
            <option value="blocked">Blocked</option>
            <option value="next_due">Next action due</option>
          </select>
          <span className="ml-1 text-xs font-bold text-[#15233B]/60" data-testid="clients-count">
            {visible.length} result{visible.length === 1 ? "" : "s"}
          </span>
          <button
            onClick={() => setSaveOpen(true)}
            data-testid="save-filter-button"
            className="ml-auto px-2.5 py-1.5 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-bold flex items-center gap-1"
          >
            <Bookmark className="h-3.5 w-3.5" /> Save filter
          </button>
        </div>

        {savedFilters.length > 0 && (
          <div className="bg-white border border-[#15233B]/10 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#15233B]/60">Saved filters</span>
            {savedFilters.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1.5 group" data-testid={`saved-filter-${f.id}`}>
                <button
                  onClick={() => applySaved(f)}
                  className="px-2.5 py-1 rounded text-xs font-semibold bg-[#F6F3EC] hover:bg-[#FBBF24] hover:text-white border border-[#15233B]/10 transition-colors flex items-center gap-1"
                  title={f.scope === "shared" ? `Shared by ${f.user_name}` : "Private"}
                >
                  {f.scope === "shared" && <Users className="h-3 w-3 text-[#15233B]/50 group-hover:text-white" />}
                  {f.name}
                </button>
                <button
                  onClick={() => deleteSaved(f.id)}
                  className="opacity-0 group-hover:opacity-100 text-[#b91c1c] hover:bg-[#b91c1c]/10 rounded p-0.5"
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {saveOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="save-filter-dialog">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[#15233B]/10 bg-[#F6F3EC]">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60">Save current filter</div>
                <h3 className="text-lg font-extrabold tracking-tight text-[#15233B]">Name this view</h3>
              </div>
              <div className="p-6 space-y-3">
                <input
                  data-testid="save-filter-name"
                  autoFocus
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g., Drij · overdue · MD stuck"
                  className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                />
                <div className="flex gap-3 text-xs font-semibold">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={saveScope === "private"} onChange={() => setSaveScope("private")} className="text-[#FBBF24]" />
                    Private (just me)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={saveScope === "shared"} onChange={() => setSaveScope("shared")} className="text-[#FBBF24]" />
                    Shared with team
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
                <button onClick={() => { setSaveOpen(false); setSaveName(""); }} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
                <button onClick={saveCurrent} data-testid="save-filter-submit" className="px-4 py-2 rounded bg-[#FBBF24] text-white text-sm font-bold">Save filter</button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#15233B]/10 rounded-md shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F3EC] text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
              <tr>
                <Th field="full_name">Client</Th>
                <Th field="current_stage">Stage</Th>
                <Th field="payer_mco">MCO</Th>
                <Th field="owner">Owner</Th>
                <Th field="days">Days in stage</Th>
                <th className="text-left px-4 py-3">State</th>
                <Th field="next_action">Next action</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#15233B]/8">
              {visible.map((c) => {
                const t = tplByStage[c.current_stage];
                const overdue = isOverdueInStage(c, t);
                const nextDue = isNextActionDue(c);
                const days = Math.floor(daysBetween(c.stage_entered_at));
                const owner = users.find((u) => u.id === c.owner_id);
                const sc = stageColor(c.current_stage);
                const oc = ownerColor(c.owner_id);
                return (
                  <tr
                    key={c.id}
                    data-testid={`clients-row-${c.av_id}`}
                    onClick={() => setSelected(c)}
                    className="hover:bg-[#F6F3EC]/60 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="h-9 w-9 shrink-0 rounded-full bg-[#F1F2F4] grid place-items-center text-[11px] font-bold text-[#15233B]/70"
                        >
                          {deriveInitials(c.full_name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-[#15233B] truncate">{c.full_name}</div>
                          <div className="text-[11px] font-mono text-[#15233B]/55">{c.av_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-testid={`cell-stage-${c.av_id}`}>
                      {editCell?.cardId === c.id && editCell?.field === "stage" ? (
                        <select
                          autoFocus
                          data-testid={`stage-select-${c.av_id}`}
                          value={c.current_stage}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => saveInlineStage(c.id, e.target.value)}
                          onBlur={closeEdit}
                          className="border border-[#FBBF24] rounded px-1.5 py-0.5 text-xs bg-white"
                        >
                          {templates.map((tt) => (
                            <option key={tt.stage_number} value={tt.stage_number}>
                              {tt.stage_number} · {tt.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          onClick={(e) => c.status === "active" && openEdit(e, c.id, "stage")}
                          className="text-[11px] font-bold inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap cursor-pointer hover:ring-2 hover:ring-[#FBBF24]/40"
                          style={{ backgroundColor: sc.primary, color: sc.text }}
                          title={c.status === "active" ? "Click to change stage" : ""}
                        >
                          <span className="font-mono opacity-80 leading-none">
                            {String(c.current_stage).padStart(2, "0")}
                          </span>
                          {t?.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-[#15233B]/70">
                      {c.payer_mco}
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`cell-owner-${c.av_id}`}>
                      {editCell?.cardId === c.id && editCell?.field === "owner" ? (
                        <select
                          autoFocus
                          data-testid={`owner-select-${c.av_id}`}
                          value={c.owner_id || ""}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => saveInlineOwner(c.id, e.target.value)}
                          onBlur={closeEdit}
                          className="border border-[#FBBF24] rounded px-1.5 py-0.5 text-xs bg-white"
                        >
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span
                          onClick={(e) => c.status === "active" && openEdit(e, c.id, "owner")}
                          className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-[#F6F3EC] rounded px-1 -mx-1"
                          title={c.status === "active" ? "Click to change owner" : ""}
                        >
                          <span
                            className="h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold shadow-sm"
                            style={{ backgroundColor: oc.primary, color: oc.text }}
                          >
                            {(owner?.name || "?").slice(0, 1)}
                          </span>
                          <span className="font-semibold text-[#15233B]">{owner?.name || "Unassigned"}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${overdue ? "bg-[#b91c1c]/10 text-[#b91c1c]" : "bg-[#F6F3EC] text-[#15233B]"}`}>
                        {overdue && <AlertTriangle className="inline h-3 w-3 mr-0.5" />}
                        {days}d / {t?.day_target}d
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {c.status === "archived" && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#15233B]/10 text-[#15233B]/70">
                            archived
                          </span>
                        )}
                        {c.blocked && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#7e22ce]/10 text-[#7e22ce] inline-flex items-center gap-1">
                            <Ban className="h-2.5 w-2.5" /> blocked
                          </span>
                        )}
                        {nextDue && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#FBBF24]/15 text-[#15233B] inline-flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" /> due
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`cell-next-action-${c.av_id}`}>
                      {editCell?.cardId === c.id && editCell?.field === "next_action" ? (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1"
                        >
                          <input
                            type="date"
                            autoFocus
                            value={editVals.next_action_date || ""}
                            onChange={(e) =>
                              setEditVals((p) => ({ ...p, next_action_date: e.target.value }))
                            }
                            data-testid={`next-action-date-${c.av_id}`}
                            className="border border-[#FBBF24] rounded px-1.5 py-0.5 text-xs bg-white"
                          />
                          <input
                            value={editVals.next_action_text || ""}
                            onChange={(e) =>
                              setEditVals((p) => ({ ...p, next_action_text: e.target.value }))
                            }
                            placeholder="what's next"
                            data-testid={`next-action-text-${c.av_id}`}
                            className="border border-[#FBBF24] rounded px-1.5 py-0.5 text-xs bg-white w-32"
                          />
                          <button
                            onClick={() => saveInlineNextAction(c.id)}
                            data-testid={`next-action-save-${c.av_id}`}
                            className="text-xs font-bold px-1.5 py-0.5 rounded bg-[#FBBF24] text-white"
                          >
                            Save
                          </button>
                          <button
                            onClick={closeEdit}
                            className="text-xs text-[#15233B]/60"
                          >
                            ✕
                          </button>
                        </div>
                      ) : c.next_action_date ? (
                        <div
                          onClick={(e) =>
                            c.status === "active" &&
                            openEdit(e, c.id, "next_action", {
                              next_action_date: c.next_action_date,
                              next_action_text: c.next_action_text || "",
                            })
                          }
                          className="cursor-pointer hover:bg-[#F6F3EC] rounded px-1 -mx-1"
                          title={c.status === "active" ? "Click to edit" : ""}
                        >
                          <div className="font-semibold text-[#15233B]">{c.next_action_date}</div>
                          <div className="text-[#15233B]/60 italic truncate max-w-[220px]">
                            {c.next_action_text || "—"}
                          </div>
                        </div>
                      ) : (
                        <span
                          onClick={(e) =>
                            c.status === "active" &&
                            openEdit(e, c.id, "next_action", { next_action_date: "", next_action_text: "" })
                          }
                          className="text-[#15233B]/30 italic cursor-pointer hover:text-[#FBBF24]"
                        >
                          + set
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-xs italic text-[#15233B]/40">
                    No clients match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <CardDetailPanel
          card={selected}
          template={tplByStage[selected.current_stage]}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelected(null)}
          onChanged={onCardChanged}
        />
      )}

      {showNewLead && (
        <NewLeadDialog
          users={users}
          lists={lists}
          templates={templates}
          onClose={() => setShowNewLead(false)}
          onCreated={(card) => { setCards((p) => [...p, card]); }}
        />
      )}
      <BrandFooter />
    </div>
  );
}
